"use client";

import { useSyncExternalStore } from "react";
import { checkDateText, formatFullDate, formatMonthDay, isoDayOf } from "@/lib/check-date";

/**
 * A check date as shoppers read it (owner 2026-10-24): "yesterday", "3 days ago", "2 weeks ago", then "Oct 3" or
 * "Oct 3, 2025" for an earlier year. The page is ISR-cached, so the server (and the first client render) write the
 * clock-independent fallback "Oct 3" (no year) and the relative text replaces it after mount: no hydration mismatch.
 * The full date stays in <time dateTime> and in the tooltip.
 *
 * `on`: write "on " before an absolute date ("... on Oct 3" / "... yesterday").
 */
// The browser's clock, read once per page load (a stable snapshot). The server snapshot is null: the server and the
// hydration render write the fallback, then React re-renders with the client value.
let clientNow: Date | null = null;
const subscribe = () => () => {};
const clientSnapshot = () => (clientNow ??= new Date());
const serverSnapshot = () => null;

export default function CheckDate({ value, on = false }: { value: string; on?: boolean }) {
    const now = useSyncExternalStore(subscribe, clientSnapshot, serverSnapshot);

    const iso = isoDayOf(value);
    const fallback = formatMonthDay(value);
    if (!iso || !fallback) return null;
    const shown = now ? checkDateText(value, now) : null;
    const text = shown ? shown.text : fallback;
    const relative = shown ? shown.relative : false;
    return (
        <>
            {on && !relative ? "on " : null}
            <time dateTime={iso} title={formatFullDate(value) ?? undefined}>{text}</time>
        </>
    );
}
